// StationChartsDetailLayout – detail stanice pro zjednodušenou stránku „Grafy stanic"
// (/station-charts/:ghId, varianta B), viz doc/plans/deploy-variant-b-station-charts.md.
// Samostatná route/komponenta, ne filtr nad StationDetailLayout — vlastní malé menu se
// 3 pevnými taby (Info / Graf poslední 2 dny / Data — denní přehled), bez date-range
// pickerů a bez ovládacích prvků nad tabulkou. Nahoře fulltext combobox pro přepnutí
// stanice bez návratu na mapu, omezený na stejnou defaultní skupinu jako mapa.
import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spinner } from '@heroui/react'
import { api } from '../api/client'
import { normalizeCzech } from '../utils/geo'
import { StationPanelDetail } from '../components/panels/StationPanelDetail'
import { ChartFrame, withJsonFormat } from '../components/reports/shared/ChartFrame'
import { DLY_COLUMNS, DayGrid } from '../components/reports/DlyOneMonthView'
import { useDlyOneMonthData } from '../hooks/useDlyOneMonthData'
import { buildChartUrl } from '../hooks/useGrafData'
import { useAuthStore } from '../store/useAuthStore'
import { useStationChartsMapStore } from '../store/useStationChartsMapStore'
import { useAllStations } from '../hooks/useAppData'
import { useChartFillBust } from '../hooks/shared/useChartFillBust'
import { useChartToFill } from '../hooks/shared/useToFill'
import { UserIcon } from '../components/auth/UserIcon'
import { addLastStationChartsStation } from '../hooks/useLastStationChartsStations'

const CHART_BASE = import.meta.env.VITE_API_URL || '/api'

// Zobrazovaný text stanice ve fulltext comboboxu — "Fullname (GH_ID) (WSI)".
function stationDisplayName(station) {
  if (!station) return ''
  const name = station.full_name || station.name
  const parts = [name, `(${station.gh_id})`]
  if (station.wsi) parts.push(`(${station.wsi})`)
  return parts.join(' ')
}

// Fulltext combobox nad taby — psaní textu filtruje stanice defaultní zjednodušené skupiny
// (stejná množina jako mapa v StationChartsLayout) přes full_name/gh_id/wsi, částečná shoda,
// diakriticky necitlivě. Vzor převzatý z PointSearchBox (MeteogramLayout.jsx).
function StationSearchBox({ stations, selectedStation, onSelect }) {
  const [inputValue, setInputValue] = useState(() => stationDisplayName(selectedStation))
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const listRef = useRef(null)

  useEffect(() => {
    setInputValue(stationDisplayName(selectedStation))
  }, [selectedStation])

  const results = useMemo(() => {
    // Rozdělit na slova a požadovat, aby VŠECHNA byla obsažena (v libovolném pořadí) — "ostrava
    // poruba" tak najde "Ostrava, Poruba", ne jen přesnou souvislou shodu celého zadaného textu.
    const words = normalizeCzech(inputValue.trim()).split(/\s+/).filter(Boolean)
    if (words.length === 0) return []
    return stations
      .filter(s => {
        const haystack = normalizeCzech(`${s.full_name || s.name || ''} ${s.gh_id || ''} ${s.wsi || ''}`)
        return words.every(w => haystack.includes(w))
      })
      .slice(0, 30)
  }, [inputValue, stations])

  const confirmSelect = (station) => {
    setInputValue(stationDisplayName(station))
    setOpen(false)
    setActiveIndex(-1)
    onSelect(station)
  }

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const idx = activeIndex >= 0 ? activeIndex : 0
      if (results[idx]) confirmSelect(results[idx])
    } else if (e.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
    }
  }

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      listRef.current.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  return (
    <div className="relative flex-1 max-w-xl">
      <input
        type="text"
        value={inputValue}
        placeholder="Hledat stanici podle názvu, ID nebo WSI…"
        className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
        onChange={e => {
          setInputValue(e.target.value)
          setActiveIndex(-1)
          setOpen(true)
        }}
        onFocus={e => { e.target.select(); setOpen(true) }}
        onBlur={() => setTimeout(() => {
          setOpen(false)
          setInputValue(stationDisplayName(selectedStation))
        }, 150)}
        onKeyDown={handleKeyDown}
      />
      {open && results.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto text-sm"
        >
          {results.map((s, i) => (
            <li
              key={s.gh_id}
              className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${i === activeIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
              onMouseDown={() => confirmSelect(s)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="font-medium">{s.full_name || s.name}</span>
              <span className="text-gray-400 text-xs font-mono">({s.gh_id})</span>
              {s.wsi && <span className="text-gray-400 text-xs font-mono">({s.wsi})</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Tab „Graf stanice" — pevné klouzavé okno posledních 2 dní do teď, žádné ovládání rozsahu.
// 10minutová granularita (nejblíže dnešnímu GrafView/10m-graf), ale bez GrafHeaderMin
// (den/okno/zarovnání).
// Stránka nemá TimeZonePicker (na rozdíl od GrafView) — čas je napevno místní
// (Europe/Prague), ne sdílený useTimeZoneStore (ten může být nastavený na UTC).
function FixedRangeChart({ station }) {
  const tz = 'Europe/Prague'
  const token = useAuthStore(s => s.token)
  const fillBust = useChartFillBust()
  const [zoomed, setZoomed] = useState(false)

  const chartUrl = withJsonFormat(
    buildChartUrl(CHART_BASE, station?.gh_id, null, 2, true, true, tz, null),
    'png'
  ) + fillBust

  const [chartMeta, setChartMeta] = useState(null)
  const screenKey = JSON.stringify(['station-charts-graf', station?.gh_id, tz])
  useChartToFill(screenKey, chartMeta, { gran: '10M', live: true }, true)

  return (
    <div className="overflow-x-auto">
      {/* Klik na graf přepne mezi zmenšeným (vejde se na šířku obrazovky) a 2x širším
          zobrazením se scrollováním — na mobilu je zmenšený multi-graf (8 panelů nad sebou)
          hůř čitelný, zvětšení pomůže bez nutnosti přegenerovat obrázek na serveru. */}
      <div
        className={`cursor-zoom-in ${zoomed ? 'w-[200%] cursor-zoom-out' : 'w-full'}`}
        onClick={() => setZoomed(z => !z)}
      >
        <ChartFrame
          key={`${chartUrl}:${token ?? 'anon'}`}
          url={chartUrl}
          gran="10m"
          interactive={false}
          onMeta={setChartMeta}
        />
      </div>
    </div>
  )
}

// Tab „Data stanice — denní přehled" — stejná tabulka jako tab „Data" na /dly-graf
// (DayGrid/DLY_COLUMNS), se zaškrtnutím „aktuální" napevno zapnuté (klouzavé okno
// posledních 30 dní končící včerejškem) a reverse=true (nejaktuálnější den nahoře) —
// na rozdíl od zbytku appky, kde je nejstarší den vždy nahoře.
function FixedDailyTable({ station }) {
  const { dataMap, labelMap, isLoading, isError } = useDlyOneMonthData(
    station?.gh_id, null, null, DLY_COLUMNS, /* isAktual */ true, /* enabled */ true
  )

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Spinner size="sm" /> Načítání dat…
      </div>
    )
  }
  if (isError) return <p className="text-red-500 text-sm">Chyba při načítání dat</p>

  return (
    <div className="h-[480px] [@media(min-height:640px)]:h-full">
      <DayGrid columns={DLY_COLUMNS} dataMap={dataMap} labelMap={labelMap} orientation="normal" reverse />
    </div>
  )
}

const TABS = [
  { key: 'graf', label: 'Graf stanice' },
  { key: 'data', label: 'Denní data' },
  { key: 'info', label: 'Info stanice' },
]

export function StationChartsDetailLayout() {
  const { ghId } = useParams()
  const navigate = useNavigate()
  const token = useAuthStore(s => s.token)
  const [station, setStation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const activeTab = useStationChartsMapStore(s => s.activeTab)
  const setActiveTab = useStationChartsMapStore(s => s.setActiveTab)

  const { allStations } = useAllStations() // server už vrací jen defaultní zjednodušenou skupinu

  useEffect(() => {
    if (!ghId) return
    setLoading(true)
    setError(null)
    api.get(`/geography?gh_id=${ghId}&join=[observation]&observation.end_date=[group]`)
      .then(data => {
        if (data.length > 0) {
          setStation(data[0])
          addLastStationChartsStation(data[0])
        } else {
          setStation(null)
          setError('Stanice nenalezena nebo není dostupná pro aktuálního uživatele')
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [ghId, token])

  const handleStationSelect = useCallback((s) => {
    navigate(`/station-charts/${s.gh_id}`)
  }, [navigate])

  return (
    <div className="flex flex-col min-h-dvh [@media(min-height:640px)]:h-dvh [@media(min-height:640px)]:overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 shrink-0">
          <button
            className="flex items-center justify-center w-9 h-9 rounded-lg text-lg text-blue-600 hover:bg-blue-50 transition-colors"
            onClick={() => navigate('/')}
            title="Domů"
          >
            🏠
          </button>
          <button
            className="flex items-center justify-center w-9 h-9 rounded-lg text-lg text-blue-600 hover:bg-blue-50 transition-colors"
            onClick={() => navigate('/station-charts')}
            title="Mapa"
          >
            🗺️
          </button>
        </div>
        <StationSearchBox stations={allStations} selectedStation={station} onSelect={handleStationSelect} />
        <div className="ml-auto shrink-0">
          <UserIcon variant="compact" />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white px-4 flex gap-2">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 [@media(min-height:640px)]:min-h-0 [@media(min-height:640px)]:overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center p-8">
            <Spinner />
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center p-8">
            <p className="text-red-500">{error}</p>
          </div>
        )}
        {station && !loading && (
          <div className="p-4 flex flex-col gap-3 [@media(min-height:640px)]:h-full">
            <div className="[@media(min-height:640px)]:flex-1 [@media(min-height:640px)]:min-h-0">
              {activeTab === 'info' && <StationPanelDetail station={station} />}
              {activeTab === 'graf' && <FixedRangeChart station={station} />}
              {activeTab === 'data' && <FixedDailyTable station={station} />}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
